import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import * as Sentry from '@sentry/react'

import { IconButton } from '@telavita-core/react-design-kit'

import { views } from '~/views/viewsCode'

import AudioLevel from '~/components/AudioLevel'
import { FeedbackMessageManager, messageCode, messageComponents } from '~/components/FeedbackMessageManager'
import { ReadyToCallMessage } from '~/components/FeedbackMessageManager/messageComponents/ReadyToCallMessage'
import RequestTokenModal from '~/components/RequestTokenModal'
import SettingsButton from '~/components/SettingsButton'
import TipsMessageBox from '~/components/TipsMessageBox'

import { DevicesContext } from '~/contexts'
import { fetchApi } from '~/handlers'
import { useNetwork, useProfile, useMedicalRecord } from '~/hooks'
import { axios, endpoints } from '~/settings'
import { ReactComponent as Logo } from '~/static/svg/logo-telavita.svg'
import { colors } from '~/utils/stylesConstants'

const WaitingRoom = ({
  updateView,
  accessToken,
  updateAccessToken,
  meeting,
  onOpenSettingsModal,
  error,
}) => {
  const videoRef = useRef()
  const history = useHistory()
  const isOnline = useNetwork()
  const { isPatient, isProfessional } = useProfile({ user: meeting?.user })

  const { medicalRecord } = useMedicalRecord({ guest: meeting?.guest }, isProfessional)
  
  const devicesContext = useContext(DevicesContext)

  const isJitsi = meeting?.schedule?.videoProvider === 'JITSI'

  const {
    userAudioTrack,
    userVideoTrack,
    handleToggleAudio,
    handleToggleVideo,
    audioEnabled,
    videoEnabled,
    mediaErrorMessage,
    devices,
  } = devicesContext

  const [loading, setLoading] = useState(false)
  const [openTokenModal, setOpenTokenModal] = useState(false)
  const [roomState, setRoomState] = useState(messageCode.PREPARING_ROOM)

  const areTracksReady = !mediaErrorMessage && devices && devices.length > 0

  useEffect(() => {
    if (error) {
      setRoomState(messageCode[error.code] || messageCode.UNKNOWN_ERROR)
    }
  }, [error])

  function closeTokenModal() {
    setLoading(false)
    setOpenTokenModal(false)
  }

  const handleRedirectToCallRoom = async (hash) => {
    try {
      if (isJitsi) {
        return updateAccessToken('ok')
      }
      const response = await axios.post(endpoints.virtualRooms.JOIN(hash))
      const accessToken = response.data.data.access_token

      updateAccessToken(accessToken)
    } catch (err) {
      Sentry.captureException(err)
    }
  }

  const joinCallRoom = async () => {
    setLoading(true)

    const { schedule } = meeting

    if (schedule) {
      if (schedule.tokenHasBeenFilled === false && isPatient) return setOpenTokenModal(true)

      handleRedirectToCallRoom(schedule.hash)
    }
  }

  useEffect(() => {
    const storageKey = '@telavita-meet/disconnectData'
    const disconnectData = JSON.parse(localStorage.getItem(storageKey))

    if (disconnectData && isOnline) {
      setTimeout(() => {
        fetchApi.sendLogToServer(disconnectData.room, {
          event: 'GUEST_DISCONNECTED',
          value: disconnectData.value,
        })
        localStorage.removeItem(storageKey)
      }, 10000)
    }
  }, [isOnline])

  useEffect(() => {
    if (userVideoTrack && videoRef.current) {
      const ms = new MediaStream()
      ms.addTrack(userVideoTrack)
      videoRef.current.srcObject = ms
    }
  }, [userVideoTrack])

  useEffect(() => {
    if (accessToken) {
      updateView(views.callRoom)
    }
  }, [accessToken, updateView])

  return (
    <>
      <div className='WaitingRoom'>
        <div className='LogoContainer'> <Logo /> </div>
        <div className='WaitingRoom__main'>
          <div className={`VideoContainerBox${videoEnabled ? '' : '--disabled'}`}>
            <div className='FeedbackMessage'>
              {(!userVideoTrack || !userAudioTrack) && !mediaErrorMessage &&
                <p style={{ color: colors.GREY_07 }}>Conectando câmera e microfone</p>
              }
              {mediaErrorMessage &&
                <p style={{ color: colors.GREY_07 }}>{mediaErrorMessage}</p>
              }
              {!videoEnabled && audioEnabled &&
                <p style={{ color: colors.WHITE }}>Câmera desligada</p>
              }
              {!videoEnabled && !audioEnabled &&
                <p style={{ color: colors.WHITE }}>Câmera e microfone desligados</p>
              }
            </div>

            <div className='LocalVideo'>
              <video
                ref={videoRef}
                playsInline
                autoPlay
                muted
              />
            </div>

            <div className='Controls'>
              <div className='Controls__buttonContainer'>
                <IconButton
                  icon={audioEnabled ? 'MicrophoneOn' : 'MicrophoneOff'}
                  variant={audioEnabled ? 'contained' : 'outlined'}
                  color={!audioEnabled ? 'danger' : null}
                  tooltip={audioEnabled ? 'Desligar microfone' : 'Ligar microfone'}
                  onClick={handleToggleAudio}
                />
              </div>

              <AudioLevel isEnabled={audioEnabled} />

              <div className='Controls__buttonContainer'>
                <IconButton
                  icon={videoEnabled ? 'CameraOn' : 'CameraOff'}
                  variant={videoEnabled ? 'contained' : 'outlined'}
                  color={!videoEnabled ? 'danger' : null}
                  tooltip={videoEnabled ? 'Desligar vídeo' : 'Ligar vídeo'}
                  onClick={handleToggleVideo}
                />
              </div>

              <div className='Controls__buttonContainer'>
                <SettingsButton onClick={onOpenSettingsModal} hasError={mediaErrorMessage}/>
              </div>
            </div>
          </div>
          {
            meeting && !mediaErrorMessage && !error
              ? <ReadyToCallMessage action={joinCallRoom} loading={loading} isProfessional={isProfessional} disabled={!areTracksReady} />
              : <FeedbackMessageManager message={messageComponents[roomState]} />
          }
        </div>

        <TipsMessageBox />
      </div>
      {openTokenModal && (
        <RequestTokenModal
          onClose={closeTokenModal}
          planGroupName={meeting.schedule.planGroupName}
          planGroupCode={meeting.schedule.planGroupCode}
          scheduleId={meeting.schedule.id}
          onRedirect={handleRedirectToCallRoom}
        />
      )}
    </>
  )
}

export default WaitingRoom
